import React, { useMemo, useState } from "react";

import { useDispatch } from "react-redux";

import { useGetFiltersQuery } from "@/features/filters/filtersApi";
import { setFilterListNameToFetch } from "@/features/filters/filtersSlice";
import {
  FilterSelectNameForPage,
  FilterSelectNamesByPage,
  FilterTypes,
} from "@/features/filters/types";

import { Select, Spin } from "antd";

import type { SelectProps } from "antd";

import type { DefaultOptionType } from "antd/es/select";

import "./FilterSelect.scss";

interface OptionType extends DefaultOptionType {
  type?: FilterTypes;
}
interface CustomSelectProps<TPage extends keyof FilterSelectNamesByPage>
  extends SelectProps<any, DefaultOptionType> {
  name: FilterSelectNameForPage<TPage>;
  onReset?: () => void;
  extraData?: OptionType[];
  filterParams?: string;
  allowFuzzySearch?: boolean;
  /** Never call GET /filters/{name}; only extraData options are shown. */
  remoteOptionsDisabled?: boolean;
}

const FilterSelect = <TPage extends keyof FilterSelectNamesByPage>({
  onChange,
  name,
  placeholder,
  onSelect,
  onClear,
  extraData = [],
  defaultValue,
  filterParams = "",
  onSearch,
  filterOption,
  allowFuzzySearch,
  remoteOptionsDisabled = false,
  value: selectedValue,
  ...rest
}: CustomSelectProps<TPage>) => {
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const dispatch = useDispatch();

  const shouldSkipQuery = () => {
    if (remoteOptionsDisabled) return true;
    if (allowFuzzySearch && !searchValue.length) return true;
    if (extraData.length > 0) return true;
    if (!menuIsOpen) return true;
    return false;
  };

  const { data: filterOptions, isLoading: isLoadingFilterOptions } =
    useGetFiltersQuery<{ data: OptionType[]; isLoading: boolean }>(
      {
        name,
        page: 1,
        params: filterParams,
      },
      { skip: shouldSkipQuery() }
    );

  const options = useMemo(() => {
    return extraData.length > 0 ? extraData : filterOptions || [];
  }, [extraData, filterOptions]);

  const notFoundContent = useMemo(() => {
    if (isLoadingFilterOptions) {
      return (
        <div className="customSelectLoading">
          <Spin />
        </div>
      );
    }
    return (
      <div className="d-flex p-3 justify-content-center">
        {name === "songs" ? "Type to search..." : "No data found"}
      </div>
    );
  }, [isLoadingFilterOptions, name]);

  const handleOnChange = (value: any, option: any) => {
    if (onChange) {
      onChange(value, option);
    }
  };

  const handleOnSearch = (value: string) => {
    setSearchValue(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleOnDropdownIsVisible = (visible: boolean) => {
    setMenuIsOpen(visible);
    dispatch(setFilterListNameToFetch(name));
  };

  return (
    <Select
      {...rest}
      showSearch
      allowClear
      className="customSelect"
      defaultValue={defaultValue}
      filterOption={filterOption}
      optionFilterProp="label"
      style={{ width: "100%" }}
      notFoundContent={notFoundContent}
      loading={isLoadingFilterOptions}
      placeholder={placeholder}
      onChange={handleOnChange}
      onSelect={onSelect}
      options={options}
      onClear={onClear}
      onSearch={handleOnSearch}
      value={selectedValue}
    />
  );
};

export default FilterSelect;
